import pluralize from "pluralize";
import React, { useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";

import { FileRow } from "../../../common/components";
import { PurposeCodeOptions, CNYPurposeCodeOptions } from "../../../constants/purposeCodes";
import { BankAccount } from "../../../types/common";
import { InvoiceState } from "../../../types/invoice";
import { formatAmount } from "../../../utils/amount";
import { formatToDate } from "../../../utils/common";

import HousingRulesText from "./HousingRulesText";
import { PaymentFormValues } from "./PaymentForm";
import {
    ApproverDetails,
    ApproverDetailsPersonalInfo,
} from "../../../types/claim";
import { PaymentApproversTable } from "./PaymentApproversTable";
import {SectionTitle} from "@blueprint/ui";

type PaymentOrderPreviewProps = {
    paymentFormValues: PaymentFormValues;
    invoice: InvoiceState;
    payerList: BankAccount[];
    paymentOrderApprovers: ApproverDetailsPersonalInfo[];
};

const PaymentOrderPreview = ({
    paymentFormValues,
    invoice,
    payerList,
    paymentOrderApprovers,
}: PaymentOrderPreviewProps): JSX.Element => {
    const selectedBankAccount = payerList.find(
//         (it) => it.bankAccountHolderName === paymentFormValues.bankAccount,
        (it) => {
            return (it.bankAccountHolderName + '-' + it.bankAccountNumber) === paymentFormValues.bankAccount
        },
    );

    const paymentApproverTableData: ApproverDetails[] = useMemo(() => {
        return paymentOrderApprovers.map((it) => ({
            details: it,
            status: undefined,
            actionTimestamp: undefined,
        }));
    }, [paymentOrderApprovers]);

    return (
        <Card className="mb-5 border-0">
            <Card.Header className={"p-0 mb-3"}>
                <h2>Payment Order</h2>
            </Card.Header>

            <Card.Body className={"p-0"}>
                <dl>
                    <SectionTitle className="mb-3" variant="h3">Payer Account Details</SectionTitle>
                    <hr className="bg-border-grey my-3" />
                    <Row xs={4}>
                        <Col className="border-end border-humble-grey">
                            <dt>
                                <p className="mt-3 mb-2 text-label-grey">
                                    Requested Date
                                </p>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {formatToDate(new Date().toISOString())}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <p className="mt-3 mb-2 text-label-grey">
                                    Payer Bank Account
                                </p>
                            </dt>
                            <dd>
                                <p className="fw-bold mb-0">
                                    {selectedBankAccount?.bankAccountHolderName}
                                </p>
                                <p className="fw-bold">
                                    {selectedBankAccount?.bankAccountNumber}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4 border-end border-humble-grey">
                            <dt>
                                <p className="mt-3 mb-2 text-label-grey">
                                    Payer Bank
                                </p>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {selectedBankAccount?.swiftAddress} -{" "}
                                    {selectedBankAccount?.bankName}
                                </p>
                            </dd>
                        </Col>
                        <Col className="px-4">
                            <dt>
                                <p className="mt-3 mb-2 text-label-grey">
                                    Payer Address
                                </p>
                            </dt>
                            <dd>
                                <p className="fw-bold">
                                    {selectedBankAccount?.registeredAddress}
                                </p>
                            </dd>
                        </Col>
                    </Row>
                    <hr className="bg-border-grey my-3" />
                    <Row>
                        <Col>
                            <SectionTitle className="mb-3" variant="h3">Payment Order Approvers</SectionTitle>
                            <PaymentApproversTable
                                data={paymentApproverTableData}
                            />
                        </Col>
                    </Row>
                    <hr className="bg-border-grey my-3" />
                    <SectionTitle className="mb-3" variant="h3">Payment Details</SectionTitle>

                    <dt><p className="text-label-grey mb-2">Payable Amount</p></dt>
                    <dd>
                        <p className="mb-5">
                            <span className="h1">
                                {formatAmount(paymentFormValues.amount)}
                            </span>
                            <span className="ms-2 fw-bold">
                                {paymentFormValues.currency}
                            </span>
                        </p>
                    </dd>
                    <hr className="bg-border-grey my-3" />

                    <dt><p className="text-label-grey mb-2">Payment Type</p></dt>
                    <dd><p className="fw-bold mb-3">-</p></dd>
                    <hr className="bg-border-grey my-3" />

                    <dt><p className="text-label-grey mb-2">Charge Bearer</p></dt>
                    <dd><p className="fw-bold mb-5">SHAR</p></dd>
                    <hr className="bg-border-grey my-3" />

                    <dt><p className="text-label-grey mb-2">Purpose Code</p></dt>
                    <dd>
                        <p className="fw-bold mb-5">
                            {
                                paymentFormValues.currency !== "CNY" ?
                                    PurposeCodeOptions.find(
                                        (option) =>
                                            option.value === paymentFormValues.purposeCode,
                                    )?.label :
                                    CNYPurposeCodeOptions.find(
                                        (option) =>
                                            option.value === paymentFormValues.purposeCode,
                                    )?.label
                            }
                        </p>
                    </dd>
                    <hr className="bg-border-grey my-3" />

                    <dt><p className="text-label-grey mb-2">Housing Rules Type</p></dt>
                    <dd>
                        <div className="mb-5">
                            <HousingRulesText
                                className="fw-bold"
                                rules={paymentFormValues.housingRulesTypes}
                            />
                        </div>
                    </dd>
                    <hr className="bg-border-grey my-3" />

                    <dt>
                        <p className="text-label-grey mb-2">
                            Remittance Information
                        </p>
                    </dt>
                    <dd>
                        <p className="text-pre-wrap fw-bold mb-5">
                            {paymentFormValues.remittanceInformation}
                        </p>
                    </dd>
                    <hr className="bg-border-grey my-3" />

                    {invoice.props.documents.length > 0 && (
                        <SectionTitle className="mb-3" variant="h3">
                            {pluralize("Invoices", invoice.props.documents.length)}{" "}
                            to be Shared to Bank
                        </SectionTitle>
                    )}
                    {invoice.props.documents.map((file, index) => (
                        <FileRow key={index} file={file} />
                    ))}
                </dl>
            </Card.Body>
        </Card>
    );
};

export default PaymentOrderPreview;
